import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  Chip,
  alpha,
} from '@mui/material';
import {
  PhotoLibrary,
  VideoLibrary,
  People as UsersIcon,
  AdminPanelSettings as StaffIcon,
  Link as LinkIcon,
  LiveTv,
  Crop as CropIcon,
  AccountBalanceWallet as PayOsIcon,
  Computer as DesktopIcon,
  Event as EventIcon,
  Contacts as ContactsIcon,
  SystemUpdate as SoftwareIcon,
  SystemUpdate as UpdateIcon,
  QrCode2 as QrCodeIcon,
  Email as EmailIcon,
  AutoAwesome as BackgroundAiIcon,
  MenuBook as MenuBookIcon,
  FilterBAndW as MonoIcon,
  PhoneAndroid,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { useI18n } from '../../i18n/useI18n';

const toneHex = (color?: string) => {
  if (!color) return '#f48fb1';
  if (color.startsWith('#') || color.startsWith('rgb')) return color;
  const map: Record<string, string> = {
    'primary.main': '#f48fb1',
    'secondary.main': '#9c27b0',
    'info.main': '#0288d1',
  };
  return map[color] || '#f48fb1';
};

interface DashboardCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  color?: string;
  index?: number;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  icon,
  title,
  description,
  onClick,
  color = 'primary.main',
  index = 0,
}) => {
  const tone = toneHex(color);

  return (
    <Box
      sx={{
        width: { xs: '50%', sm: '50%', md: '33.33%' },
        p: { xs: 0.7, sm: 1 },
        animation: 'dashCardIn 0.45s ease both',
        animationDelay: `${Math.min(index, 10) * 0.04}s`,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        sx={{
          p: { xs: 1.4, sm: 2.25 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 1.25 },
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          minHeight: { xs: 118, sm: 168 },
          borderRadius: { xs: 3, sm: 3.5 },
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: alpha(tone, 0.18),
          boxShadow: `0 8px 22px ${alpha('#bf5f82', 0.07)}`,
          backgroundImage: `linear-gradient(165deg, #ffffff 0%, ${alpha(tone, 0.07)} 100%)`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          outline: 'none',
          '&:hover': {
            transform: 'translateY(-3px)',
            borderColor: alpha(tone, 0.4),
            boxShadow: `0 14px 28px ${alpha(tone, 0.18)}`,
          },
          '&:active': {
            transform: 'translateY(-1px) scale(0.985)',
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 3px ${alpha(tone, 0.35)}`,
          },
        }}
      >
        <Box
          sx={{
            width: { xs: 48, sm: 58 },
            height: { xs: 48, sm: 58 },
            borderRadius: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(tone, 0.14),
            color: tone,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 26, sm: 30 },
            },
          }}
        >
          {icon}
        </Box>
        <Typography
          component="h2"
          align="center"
          sx={{
            fontSize: { xs: '0.82rem', sm: '1.05rem' },
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            color: '#2d2430',
            px: 0.25,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{
            display: { xs: 'none', sm: 'block' },
            color: alpha('#2d2430', 0.55),
            fontSize: '0.8rem',
            lineHeight: 1.35,
            maxWidth: 220,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { dashboard: t, formatMessage } = useI18n();
  const [mobileUploadEnabled, setMobileUploadEnabled] = useState(false);
  const [aiCreateEnabled, setAiCreateEnabled] = useState(false);
  const [loadingMobileUploadStatus, setLoadingMobileUploadStatus] = useState(true);
  const deviceAccess = {
    pro: user?.deviceAccess?.pro ?? true,
    lite: user?.deviceAccess?.lite ?? false,
    mono: user?.deviceAccess?.mono ?? false,
  };

  useEffect(() => {
    const checkMobileUploadStatus = async () => {
      try {
        if (user?.role === 'USER' || user?.role === 'STAFF_USER') {
          const response = await api.getMobileUploadStatus();
          setMobileUploadEnabled(response.mobileUploadEnabled || false);
          try {
            const ai = await api.getBackgroundAiStatus();
            setAiCreateEnabled(Boolean(ai.enabled));
          } catch {
            setAiCreateEnabled(false);
          }
        } else {
          setMobileUploadEnabled(true);
        }
      } catch (error) {
        console.error('Error checking mobile upload status:', error);
        setMobileUploadEnabled(false);
      } finally {
        setLoadingMobileUploadStatus(false);
      }
    };

    if (user) {
      checkMobileUploadStatus();
    }
  }, [user]);

  const f = t.features;

  const adminFeatures = [
    {
      title: f.userManagement.title,
      description: f.userManagement.description,
      icon: <UsersIcon />,
      path: '/users',
      color: '#1976d2',
    },
    {
      title: f.staffManagement.title,
      description: f.staffManagement.description,
      icon: <StaffIcon />,
      path: '/staff',
      color: '#2e7d32',
    },
    {
      title: f.customFrame.title,
      description: f.customFrame.description,
      icon: <CropIcon />,
      path: '/custom-frames',
      color: '#ff9800',
    },
    {
      title: f.album.title,
      description: f.album.description,
      icon: <MenuBookIcon />,
      path: '/admin/public-albums',
      color: '#5c6bc0',
    },
    {
      title: f.landingPage.title,
      description: f.landingPage.description,
      icon: <LinkIcon />,
      path: '/admin/landing-page',
      color: '#00838f',
    },
    {
      title: f.desktopManagement.title,
      description: f.desktopManagement.description,
      icon: <DesktopIcon />,
      path: '/admin/desktops',
      color: '#0288d1',
    },
    {
      title: f.updateSoftware.title,
      description: f.updateSoftware.description,
      icon: <UpdateIcon />,
      path: '/admin/software-update',
      color: '#f57c00',
    },
    {
      title: f.emailManagement.title,
      description: f.emailManagement.description,
      icon: <EmailIcon />,
      path: '/admin/email-composer',
      color: '#e91e63',
    },
    {
      title: f.backgroundAi.title,
      description: f.backgroundAi.description,
      icon: <BackgroundAiIcon />,
      path: '/admin/background-ai',
      color: '#7e57c2',
    },
  ];

  const staffFeatures = [
    {
      title: f.userManagement.title,
      description: f.userManagement.description,
      icon: <UsersIcon />,
      path: '/users',
      color: '#1976d2',
    },
    {
      title: f.customFrame.title,
      description: f.customFrame.description,
      icon: <CropIcon />,
      path: '/custom-frames',
      color: '#ff9800',
    },
    {
      title: f.album.title,
      description: f.album.description,
      icon: <MenuBookIcon />,
      path: '/admin/public-albums',
      color: '#5c6bc0',
    },
    {
      title: f.landingPage.title,
      description: f.landingPage.description,
      icon: <LinkIcon />,
      path: '/admin/landing-page',
      color: '#00838f',
    },
  ];

  const userFeatures = [
    {
      title: f.mPhotoPro.title,
      description: f.mPhotoPro.description,
      icon: <PhotoLibrary />,
      path: '/m-photo-pro',
      color: 'primary.main',
    },
    {
      title: f.mPhotoLite.title,
      description: f.mPhotoLite.description,
      icon: <PhoneAndroid />,
      path: '/m-photo-lite',
      color: '#00897b',
    },
    {
      title: f.mPhotoMono.title,
      description: f.mPhotoMono.description,
      icon: <MonoIcon />,
      path: '/m-photo-mono',
      color: '#7c4dff',
    },
    {
      title: f.deviceManagement.title,
      description: f.deviceManagement.description,
      icon: <QrCodeIcon />,
      path: '/mobile-upload',
      color: '#3498db',
    },
    {
      title: f.userStaff.title,
      description: f.userStaff.description,
      icon: <GroupsIcon />,
      path: '/staff-users',
      color: '#5c6bc0',
    },
    {
      title: f.livePhotos.title,
      description: f.livePhotos.description,
      icon: <LiveTv />,
      path: '/live-photos',
      color: 'info.main',
    },
    {
      title: f.videos.title,
      description: f.videos.description,
      icon: <VideoLibrary />,
      path: '/videos',
      color: 'secondary.main',
    },
    {
      title: f.sePayPayment.title,
      description: f.sePayPayment.description,
      icon: <PayOsIcon />,
      path: '/payment-payos',
      color: '#00897b',
    },
    {
      title: f.eventManagement.title,
      description: f.eventManagement.description,
      icon: <EventIcon />,
      path: '/events',
      color: '#f50057',
    },
    {
      title: f.customerInformation.title,
      description: f.customerInformation.description,
      icon: <ContactsIcon />,
      path: '/customer-information',
      color: '#00bcd4',
    },
    {
      title: f.software.title,
      description: f.software.description,
      icon: <SoftwareIcon />,
      path: '/software',
      color: '#f57c00',
    },
    {
      title: f.aiCreate.title,
      description: f.aiCreate.description,
      icon: <BackgroundAiIcon />,
      path: '/ai-themes',
      color: '#7e57c2',
    },
  ];

  const staffUserFeatures = [
    {
      title: f.mPhotoPro.title,
      description: f.mPhotoPro.description,
      icon: <PhotoLibrary />,
      path: '/m-photo-pro',
      color: 'primary.main',
    },
    {
      title: f.mPhotoLite.title,
      description: f.mPhotoLite.description,
      icon: <PhoneAndroid />,
      path: '/m-photo-lite',
      color: '#00897b',
    },
    {
      title: f.mPhotoMono.title,
      description: f.mPhotoMono.description,
      icon: <MonoIcon />,
      path: '/m-photo-mono',
      color: '#7c4dff',
    },
    {
      title: f.deviceManagement.title,
      description: f.deviceManagement.description,
      icon: <QrCodeIcon />,
      path: '/mobile-upload',
      color: '#3498db',
    },
    {
      title: f.aiCreate.title,
      description: f.aiCreate.description,
      icon: <BackgroundAiIcon />,
      path: '/ai-themes',
      color: '#7e57c2',
    },
  ];

  const getFeatures = () => {
    let features;
    switch (user?.role) {
      case 'ADMIN':
        features = adminFeatures;
        break;
      case 'STAFF':
        features = staffFeatures;
        break;
      case 'STAFF_USER':
        features = staffUserFeatures;
        break;
      default:
        features = userFeatures;
        break;
    }

    if ((user?.role === 'USER' || user?.role === 'STAFF_USER') && !loadingMobileUploadStatus && !mobileUploadEnabled) {
      features = features.filter((feature) => feature.path !== '/mobile-upload');
    }

    if ((user?.role === 'USER' || user?.role === 'STAFF_USER') && !aiCreateEnabled) {
      features = features.filter((feature) => feature.path !== '/ai-themes');
    }

    if (user?.role === 'USER' || user?.role === 'STAFF_USER') {
      if (!deviceAccess.pro) {
        features = features.filter((feature) => feature.path !== '/m-photo-pro');
      }
      if (!deviceAccess.mono) {
        features = features.filter((feature) => feature.path !== '/m-photo-mono');
      }
      if (!deviceAccess.lite) {
        features = features.filter((feature) => feature.path !== '/m-photo-lite');
      }
      if (!(deviceAccess.pro || deviceAccess.lite || deviceAccess.mono)) {
        features = features.filter((feature) => feature.path !== '/software');
      }
    }

    return features;
  };

  const features = getFeatures();
  const roleLabel =
    user?.role === 'ADMIN'
      ? 'Admin'
      : user?.role === 'STAFF'
        ? 'Staff'
        : user?.role === 'STAFF_USER'
          ? 'Staff'
          : 'User';

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 64px)', sm: 'auto' },
        background: `
          radial-gradient(ellipse 80% 50% at 0% -10%, ${alpha('#f48fb1', 0.28)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 100% 0%, ${alpha('#ffc1e3', 0.35)} 0%, transparent 50%),
          linear-gradient(180deg, #fff8fa 0%, #ffffff 55%)
        `,
      }}
    >
      <Container
        maxWidth="lg"
        className="user-page-layout user-page-enter"
        sx={{ pt: { xs: 2, sm: 3.5 }, pb: { xs: 4, sm: 5 }, px: { xs: 1.5, sm: 3 } }}
      >
        <Box
          sx={{
            mb: { xs: 2, sm: 3 },
            p: { xs: 1.75, sm: 2.5 },
            borderRadius: { xs: 3, sm: 4 },
            border: '1px solid',
            borderColor: alpha('#f48fb1', 0.22),
            background: `linear-gradient(135deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#ffc1e3', 0.28)} 100%)`,
            boxShadow: `0 10px 28px ${alpha('#bf5f82', 0.08)}`,
          }}
        >
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              mb: 1,
              height: 24,
              fontWeight: 700,
              fontSize: '0.7rem',
              bgcolor: alpha('#bf5f82', 0.12),
              color: '#bf5f82',
              border: 'none',
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '1.45rem', sm: '2rem' },
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.15,
              color: '#2d2430',
              mb: 0.5,
            }}
          >
            {formatMessage(t.welcome, { username: user?.username })}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
              color: alpha('#2d2430', 0.55),
              lineHeight: 1.4,
            }}
          >
            {features.length} mục · chọn để mở nhanh
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: { xs: -0.7, sm: -1 } }}>
          {features.map((feature, index) => (
            <DashboardCard
              key={feature.path}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              onClick={() => navigate(feature.path)}
              color={feature.color}
              index={index}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
